import classNames from "classnames"
import { DefaultJsxChildrenProp } from "themes/default-tailwind/types"
import { FireAiLoginLogoProps } from "themes/default-tailwind/branding/FireAiLoginLogo"
import { useMediaQuery } from "react-responsive"
import { CSSProperties, type JSX } from "react"
import { ButtonAuthenticationTertiary } from "@nova/component-btn"
import { AuthenticationGallery } from "./gallery/AuthenticationGallery"

export type AuthenticationManagementContainerProps = {
    header?: (source: FireAiLoginLogoProps) => JSX.Element
    children?: DefaultJsxChildrenProp
    title?: string
    subtitle?: string
    subtitleAction?: {
        title: string
        onClick: () => void
    }
    classes?: string
    style?: CSSProperties
    centerChildren?: boolean
    centerTitle?: boolean
    showDisclaimer?: boolean
    wide?: boolean
}

/**
 * sets up the basic container structure/styles to hold our login content
 */
export default function AuthenticationManagementContainer({
    header: HeaderComponent,
    children,
    title,
    subtitle,
    subtitleAction,
    style,
    centerChildren = true,
    centerTitle,
    showDisclaimer,
    wide,
}: AuthenticationManagementContainerProps) {
    const isMobile = useMediaQuery({ maxWidth: 768 })
    const isSmall = useMediaQuery({ maxWidth: 1024 })
    const isMedium = useMediaQuery({ maxWidth: 1200 })
    const isNormal = useMediaQuery({ minWidth: 1201 })

    const responsiveWidthClasses = classNames({
        "w-[400px]": isSmall,
        "w-[430px]": isMedium && !isSmall,
        [wide ? "w-[582px]" : "w-[540px]"]: isNormal,
    })
    return (
        <div
            className="background authentication-management-container h-screen overflow-y-auto flex justify-center !bg-[#F8F8F8]"
            style={style}
        >
            <div
                className={classNames(
                    "auth-content relative max-w-[1000px] text-left pt-[100px] box-content",
                    { "w-[50vw]": !isMobile }
                )}
            >
                <div
                    className={classNames(
                        "mx-auto flex flex-col min-h-full",
                        responsiveWidthClasses
                    )}
                >
                    <div className="header flex justify-between items-center">
                        <div className="w-[110px]">{HeaderComponent && <HeaderComponent />}</div>
                        {subtitle && (
                            <div className="flex items-center gap-2">
                                <div className="text-[#5E5A5B] text-[14px] leading-6">
                                    {subtitle}
                                </div>
                                {subtitleAction && (
                                    <ButtonAuthenticationTertiary
                                        title={subtitleAction.title}
                                        color="#33171E"
                                        onClick={subtitleAction.onClick}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                    <div
                        className={classNames(
                            {
                                "my-auto py-5": wide,
                                "absolute top-[50vh] -translate-y-[50%]": centerChildren && !wide,
                                "mt-[20px]": !centerChildren,
                            },
                            responsiveWidthClasses
                        )}
                    >
                        <h2
                            className={classNames(
                                "text-[54px] leading-[60px] text-night mb-9 text-left telegraf",
                                { "!text-[40px]": isMedium },
                                { "!text-center": centerTitle }
                            )}
                        >
                            {title}
                        </h2>
                        {children}
                    </div>
                    {showDisclaimer && (
                        <div
                            className={classNames(
                                "footer flex justify-between items-center",
                                wide ? "mt-auto pt-9 pb-[41px]" : "absolute bottom-[41px]"
                            )}
                        >
                            <p className="text-[14px] leading-[24px] text-black-700">
                                By creating an account you agree to the{" "}
                                <a
                                    href="https://www.mapnova.com/terms-of-service"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="underline hover:!text-[#406354]"
                                >
                                    Terms of Service
                                </a>{" "}
                                and{" "}
                                <a
                                    href="https://www.mapnova.com/privacy"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="underline hover:!text-[#406354]"
                                >
                                    Privacy Policy
                                </a>
                            </p>
                        </div>
                    )}
                </div>
            </div>
            {!isMobile && <AuthenticationGallery />}
        </div>
    )
}
